import { useCallback } from 'react';
import { UseFieldFormattingReturn } from './GenericFormTypes';

/**
 * Field Formatting Hook
 * Provides formatting logic for different field types
 */

export const useFieldFormatting = (): UseFieldFormattingReturn => {
  // =============================================================================
  // Phone Number Formatting
  // =============================================================================

  const formatPhoneDisplay = (value: string): string => {
    if (!value) {
      return '';
    }

    // Remove all non-digits first
    const digits = value.replace(/[^0-9]/g, '');

    // Apply mask: 5XX XXX XX XX
    if (digits.length >= 1) {
      let formatted = digits[0];
      if (digits.length >= 2) {
        formatted += digits.slice(1, 3);
      }
      if (digits.length >= 4) {
        formatted += ' ' + digits.slice(3, 6);
      }
      if (digits.length >= 7) {
        formatted += ' ' + digits.slice(6, 8);
      }
      if (digits.length >= 9) {
        formatted += ' ' + digits.slice(8, 10);
      }
      return formatted;
    }
    return value;
  };

  const formatPhoneInput = (value: string): string => {
    // Remove all non-digits and spaces
    const digits = value.replace(/[^0-9]/g, '');
    // Limit to 10 digits
    return digits.length <= 10 ? digits : digits.slice(0, 10);
  };

  // =============================================================================
  // TC Identity Number Formatting
  // =============================================================================

  const formatTCInput = (value: string): string => {
    const digits = value.replace(/[^0-9]/g, '');
    return digits.length <= 11 ? digits : digits.slice(0, 11);
  };

  // =============================================================================
  // Name Formatting
  // =============================================================================

  const formatNameInput = (value: string): string => {
    // Allow only Turkish and English letters and spaces
    return value.replace(/[^a-zA-ZçğıöşüÇĞIİÖŞÜ\s]/g, '');
  };

  // =============================================================================
  // Main Formatting Functions
  // =============================================================================

  const formatDisplayValue = useCallback((value: string, fieldKey: string): string => {
    if (!value) {
      return '';
    }

    switch (fieldKey) {
      case 'phone_number':
        return formatPhoneDisplay(value);
      default:
        return value;
    }
  }, []);

  const formatInputValue = useCallback((value: string, fieldKey: string): string => {
    if (!value) {
      return '';
    }

    switch (fieldKey) {
      case 'phone_number':
        return formatPhoneInput(value);
      case 'identification_number':
        return formatTCInput(value);
      case 'name':
      case 'surname':
        return formatNameInput(value);
      default:
        return value;
    }
  }, []);

  const isValidInput = useCallback((value: string, fieldKey: string): boolean => {
    if (!value) {
      return true;
    }

    switch (fieldKey) {
      case 'phone_number':
        const digits = value.replace(/[^0-9]/g, '');
        return digits.length <= 10;
      case 'identification_number':
        const tcDigits = value.replace(/[^0-9]/g, '');
        return tcDigits.length <= 11;
      case 'name':
      case 'surname':
        return /^[a-zA-ZçğıöşüÇĞIİÖŞÜ\s]*$/.test(value);
      default:
        return true;
    }
  }, []);

  return {
    formatDisplayValue,
    formatInputValue,
    isValidInput,
  };
};
